import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  Checkbox,
  TablePagination,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../utils/httpClientTs';
import {
  addChecksToBatch,
  downloadCheckBatchPdf,
  finalizeCheckBatch,
  getCheckBatch,
  getCheckPayments,
  removeCheckFromBatch,
} from '../../endpoints/financePayments';
import type { CheckPaymentDTO } from '../../types/financePayments';

const PICK_PAGE_SIZE = 25;

/** Stable fallback so `pendingRows` is not a new [] each render when data is absent. */
const EMPTY_CHECK_PAYMENTS: CheckPaymentDTO[] = [];

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

/** Matches backend: prefix before first ':', or full trimmed string. */
function sourceBucket(checkSource?: string): string {
  const s = (checkSource ?? '').trim();
  if (!s) return '(unknown)';
  const i = s.indexOf(':');
  return i === -1 ? s : s.slice(0, i).trim();
}

function shortId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}

const PaymentsCheckBatchDetailPage: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const [pickPage, setPickPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data, isLoading, refetch } = useApiGet(
    ['finance-payments-batch', batchId],
    () => getCheckBatch(batchId!),
    { enabled: !!batchId, retry: 1 }
  );

  const batch = data?.batch;
  const pickEnabled = !!batchId && batch?.status === 'pending';

  const { data: pickList, refetch: refetchPickList } = useApiGet(
    ['finance-payments-pick-list', batchId, pickPage],
    () =>
      getCheckPayments({
        page: pickPage + 1,
        page_size: PICK_PAGE_SIZE,
        status: 'pending',
        unbatched_only: true,
      }),
    { enabled: pickEnabled, retry: 1 }
  );

  useEffect(() => {
    setPickPage(0);
    setSelected(new Set());
  }, [batchId]);

  const pendingRows: CheckPaymentDTO[] = pickList?.data ?? EMPTY_CHECK_PAYMENTS;
  const pickTotal = pickList?.pagination?.total_count ?? 0;

  const pageIds = useMemo(() => pendingRows.map((c) => c.id), [pendingRows]);

  const numSelectedOnPage = useMemo(
    () => pageIds.filter((id) => selected.has(id)).length,
    [pageIds, selected]
  );

  const allOnPageSelected =
    pageIds.length > 0 && numSelectedOnPage === pageIds.length;
  const someOnPageSelected =
    numSelectedOnPage > 0 && numSelectedOnPage < pageIds.length;

  const { mutate: addChecks, isPending: adding } = useApiSend(
    (ids: string[]) => addChecksToBatch(batchId!, ids),
    () => {
      setErrorMsg(null);
      setSelected(new Set());
      void refetch();
      void refetchPickList();
    },
    (err) => setErrorMsg(getApiErrorMessage(err))
  );

  const { mutate: removeCheck, isPending: removing } = useApiSend(
    (checkId: string) => removeCheckFromBatch(batchId!, checkId),
    () => {
      setErrorMsg(null);
      void refetch();
      void refetchPickList();
    },
    (err) => setErrorMsg(getApiErrorMessage(err))
  );

  const { mutate: finalizeMutate, isPending: finalizing } = useApiSend(
    () => finalizeCheckBatch(batchId!),
    () => {
      setErrorMsg(null);
      void refetch();
    },
    (err) => setErrorMsg(getApiErrorMessage(err))
  );

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleAllOnPage = (checked: boolean) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (checked) {
        pageIds.forEach((id) => n.add(id));
      } else {
        pageIds.forEach((id) => n.delete(id));
      }
      return n;
    });
  };

  const handleAddSelected = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      setErrorMsg('Select at least one check using the checkboxes.');
      return;
    }
    addChecks(ids);
  };

  const handlePrint = async () => {
    try {
      setErrorMsg(null);
      await downloadCheckBatchPdf(batchId!);
    } catch (e) {
      setErrorMsg(getApiErrorMessage(e));
    }
  };

  if (!batchId) {
    return <Typography>Missing batch id.</Typography>;
  }

  const checks = data?.checks ?? [];

  const selectedCount = selected.size;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/finance/payments/check-batches')}
        sx={{ mb: 2 }}
      >
        Back to batches
      </Button>

      {errorMsg && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setErrorMsg(null)}
        >
          {errorMsg}
        </Alert>
      )}

      {isLoading && <Typography>Loading…</Typography>}

      {batch && (
        <>
          <Typography variant="h5" sx={{ mb: 1 }}>
            {batch.batch_number}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Status: <strong>{batch.status}</strong> · Checks:{' '}
            {batch.check_count} · Total: {fmtMoney(batch.total_amount)}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => void handlePrint()}
            >
              Print / download PDF
            </Button>
            {batch.status === 'pending' && (
              <Button
                color="success"
                variant="contained"
                disabled={finalizing || checks.length === 0}
                onClick={() => finalizeMutate(undefined)}
              >
                Finalize deposit
              </Button>
            )}
          </Box>

          {batch.status === 'pending' && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Add pending checks
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select unbatched pending checks (same style as a pick list). Use
                pagination to move through the queue.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Button
                  variant="contained"
                  disabled={adding || selectedCount === 0}
                  onClick={handleAddSelected}
                >
                  Add selected to batch
                  {selectedCount > 0 ? ` (${selectedCount})` : ''}
                </Button>
                {selectedCount > 0 && (
                  <Button size="small" onClick={() => setSelected(new Set())}>
                    Clear selection
                  </Button>
                )}
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected}
                        onChange={(_, checked) => toggleAllOnPage(checked)}
                        inputProps={{
                          'aria-label': 'select all pending on this page',
                        }}
                      />
                    </TableCell>
                    <TableCell>Check #</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Memo / full source</TableCell>
                    <TableCell>ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingRows.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.has(c.id)}
                          onChange={() => toggleOne(c.id)}
                          inputProps={{ 'aria-label': `select ${c.id}` }}
                        />
                      </TableCell>
                      <TableCell>{c.check_number}</TableCell>
                      <TableCell align="right">{fmtMoney(c.amount)}</TableCell>
                      <TableCell>{sourceBucket(c.check_source)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 280 }}>
                          {(c.check_source ?? '').trim() || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          title={c.id}
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {shortId(c.id)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {pendingRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        No unbatched pending checks. New mail-in orders will
                        appear here.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={pickTotal}
                page={pickPage}
                onPageChange={(_, p) => setPickPage(p)}
                rowsPerPage={PICK_PAGE_SIZE}
                rowsPerPageOptions={[PICK_PAGE_SIZE]}
                onRowsPerPageChange={() => {}}
              />
            </Paper>
          )}

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Check #</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Source</TableCell>
                  {batch.status === 'pending' && (
                    <TableCell align="right">Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {checks.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.check_number}</TableCell>
                    <TableCell align="right">{fmtMoney(c.amount)}</TableCell>
                    <TableCell>{c.status}</TableCell>
                    <TableCell>{c.check_source || '—'}</TableCell>
                    {batch.status === 'pending' && (
                      <TableCell align="right">
                        {c.status === 'received' && (
                          <Button
                            size="small"
                            disabled={removing}
                            onClick={() => removeCheck(c.id)}
                          >
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {checks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      No checks in this batch yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default PaymentsCheckBatchDetailPage;
